// Path: src/components/aols/aeo/OLShealth/Packages.tsx
// feat: Created new Packages page to display recommended user journey scenarios.
// Implemented a reusable, responsive PackageCard component based on user-provided style.
// Styled with OLS luminous color theme and Ant Design icons.
// 06/08/25 11:50 AM +07

import React, { Fragment } from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Typography, Breadcrumb } from 'antd';
import { UserOutlined, GoldOutlined, ExperimentOutlined, MedicineBoxOutlined, HeartOutlined, StarOutlined, CheckCircleOutlined, DollarCircleOutlined, HomeOutlined } from '@ant-design/icons';

const { Title, Paragraph } = Typography;

// --- Data for the Packages ---
const packagesData = [
  {
    id: 1,
    isRecommended: true,
    title: 'OLS Student',
    info: 'The most comprehensive path for full immersion into the Sun Light Civilization.',
    price: 'Custom Evaluation',
    actionLink: '/OLSstudentprospect',
    features: [
      { icon: <UserOutlined />, text: 'Become a full <strong>OLS Student</strong>.' },
      { icon: <ExperimentOutlined />, text: 'Includes the complete <strong>olsme</strong> package.' },
      { icon: <HeartOutlined />, text: 'Includes the <strong>OLS Health Monitor</strong>.' },
      { icon: <CheckCircleOutlined />, text: 'Qualify for discounts on all <strong>OLS Health</strong> packages.' },
    ],
  },
  {
    id: 2,
    title: 'OLSLM Assets',
    info: 'For those focused on acquiring and managing OLS tangible light assets.',
    price: 'Asset Purchase',
    actionLink: '/aOLSLMasset',
    features: [
      { icon: <GoldOutlined />, text: 'Obtain valuable <strong>OLSLM Assets</strong>.' },
      { icon: <ExperimentOutlined />, text: 'Includes <strong>olsme</strong> subscription benefits.' },
      { icon: <HeartOutlined />, text: '<strong>OLS Health Monitor</strong> included for FREE in 2025.' },
      { icon: <CheckCircleOutlined />, text: 'Qualify for discounts on all <strong>OLS Health</strong> packages.' },
    ],
  },
  {
    id: 3,
    title: 'OLSME Subscription',
    info: 'Access the core meditative energy and community features of the OLS platform.',
    price: 'Monthly Subscription',
    actionLink: '/olsmeSub',
    features: [
      { icon: <ExperimentOutlined />, text: 'Full access to <strong>olsme</strong> platform.' },
      { icon: <HeartOutlined />, text: '<strong>OLS Health Monitor</strong> included for FREE in 2025.' },
      { icon: <CheckCircleOutlined />, text: 'Qualify for discounts on all <strong>OLS Health</strong> packages.' },
    ],
  },
  {
    id: 4,
    title: 'OLS Health Starter',
    info: 'A focused 100-day program to begin your self-cleansing and healing journey.',
    price: 'Package Price',
    actionLink: '/OLShealth',
    features: [
      { icon: <MedicineBoxOutlined />, text: 'Enroll in the <strong>100-Day Starter Package</strong>.' },
      { icon: <HeartOutlined />, text: '<strong>OLS Health Monitor</strong> included for FREE in 2025.' },
    ],
  },
  {
    id: 5,
    title: 'Healing Therapy Intro',
    info: 'A direct entry point into the OLS methodology for transformative healing.',
    price: 'Est. 1 OLS Tuition',
    actionLink: '/OLShealth',
    features: [
      { icon: <MedicineBoxOutlined />, text: 'Enroll in the <strong>Healing Therapy Intro Package</strong>.' },
      { icon: <DollarCircleOutlined />, text: 'Value estimated at one OLS student tuition fee.' },
    ],
  },
];

// --- Reusable Package Card Component ---
const PackageCardWrapper = styled.div`
  .plan {
    border-radius: 16px;
    box-shadow: 0 30px 30px -25px rgba(65, 51, 183, 0.25);
    padding: 10px;
    background-color: #000;
    color: #d3d3d3;
    max-width: 360px;
    width: 100%;
    position: relative;
    overflow: hidden;
    border: 1px solid #333;
  }
  .plan strong {
    font-weight: 600;
    color: #fff;
  }
  .plan .inner {
    align-items: center;
    padding: 20px;
    padding-top: 40px;
    background-color: #1a1a1a;
    border-radius: 12px;
    position: relative;
  }
  .plan .pricing {
    position: absolute;
    top: 0;
    right: 0;
    background-color: #0427f4;
    border-radius: 99em 0 0 99em;
    display: flex;
    align-items: center;
    padding: 0.625em 0.75em;
    font-size: 1.25rem;
    font-weight: 600;
    color: #fff;
  }
  .plan .title {
    font-weight: 700;
    font-size: 1.5rem;
    color: #ffcc00;
    text-align: center;
  }
  .plan .title + * { margin-top: 0.75rem; }
  .plan .info { text-align: center; }
  .plan .info + * { margin-top: 1rem; }
  .plan .features {
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0;
    list-style-type: none;
  }
  .plan .features li {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-top: 0.75rem;
  }
  .plan .features li:first-child {
      margin-top: 0;
  }
  .plan .features .icon {
    background-color: #ffaa00;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #000;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    font-size: 14px;
    flex-shrink: 0;
  }
  .plan .features + * { margin-top: 1.25rem; }
  .plan .action {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: end;
  }
  .plan .button {
    background-color: #ffaa00;
    border-radius: 6px;
    color: #000;
    font-weight: 600;
    font-size: 1.125rem;
    text-align: center;
    border: 0;
    outline: 0;
    width: 100%;
    padding: 0.75em 1em;
    text-decoration: none;
    transition: background-color 0.3s ease;
  }
  .plan .button:hover, .plan .button:focus {
    background-color: #ffcc00;
  }
  .recommended-banner {
    position: absolute;
    top: 18px;
    left: -40px;
    background-color: #ffcc00;
    color: #000;
    font-weight: bold;
    padding: 5px 40px;
    transform: rotate(-45deg);
    font-size: 12px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 5px;
  }
`;

interface PackageCardProps {
  isRecommended?: boolean;
  title: string;
  info: string;
  price: string;
  features: { icon: React.ReactNode; text: string }[];
  actionLink: string;
}

const PackageCard: React.FC<PackageCardProps> = ({ isRecommended, title, info, price, features, actionLink }) => {
  return (
    <PackageCardWrapper>
      <div className="plan">
        {isRecommended && <div className="recommended-banner"><StarOutlined /> Most Recommended</div>}
        <div className="inner">
          <span className="pricing">
            <span>{price}</span>
          </span>
          <p className="title">{title}</p>
          <p className="info" dangerouslySetInnerHTML={{ __html: info }} />
          <ul className="features">
            {features.map((feature, index) => (
              <li key={index}>
                <span className="icon">{feature.icon}</span>
                <span dangerouslySetInnerHTML={{ __html: feature.text }} />
              </li>
            ))}
          </ul>
          <div className="action">
            <Link className="button" to={actionLink}>
              Learn More
            </Link>
          </div>
        </div>
      </div>
    </PackageCardWrapper>
  );
}

// --- Main Page Component ---
const PageWrapper = styled.div`
  background: #000;
  color: #fff;
  min-height: 100vh;
  padding: 60px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PackagesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 40px;
  margin-top: 40px;
  width: 100%;
`;

const BreadcrumbWrapper = styled.div`
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px 20px 20px;
  text-align: left;
  .ant-breadcrumb-link a, .ant-breadcrumb-separator {
    color: #ffcc00;
    transition: color 0.3s ease;
  }
  .ant-breadcrumb-link a:hover {
    color: #ffaa00;
  }
  .ant-breadcrumb-link, .ant-breadcrumb-separator {
    font-size: 14px;
  }
`;

const Packages: React.FC = () => {
  return (
    <Fragment>
      <Helmet>
        <title>OLS Packages & User Journeys | OneLightSystem</title>
        <meta
          name="description"
          content="Explore the recommended packages and paths to join the OneLightSystem OLS community. Choose from OLS Student, OLSLM Assets, OLSME Subscription, and OLShealth programs."
        />
        <meta
          name="keywords"
          content="OLS Packages, OLS Student, OLSLM, OLSME, OLShealth, Sun Light Meditation, Join OLS, Meditation Education"
        />
        <link rel="canonical" href="https://www.olsme.com/OLShealth/packages" />
      </Helmet>
      <PageWrapper>
        <BreadcrumbWrapper>
            <Breadcrumb>
                <Breadcrumb.Item>
                    <Link to="/"><HomeOutlined /> Home</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <Link to="/OLShealth">OLShealth</Link>
                </Breadcrumb.Item>
                <Breadcrumb.Item style={{ color: '#fff' }}>Packages</Breadcrumb.Item>
            </Breadcrumb>
        </BreadcrumbWrapper>
        <Title level={1} style={{ color: '#ffcc00', textAlign: 'center' }}>
          Recommended OLS Journeys
        </Title>
        <Paragraph style={{ color: '#d3d3d3', textAlign: 'center', maxWidth: '700px' }}>
          There are several pathways to begin your radiant journey with OneLightSystem. Below are our most recommended scenarios, designed to suit different goals and levels of commitment.
        </Paragraph>
        <PackagesContainer>
          {packagesData.map(pkg => (
            <PackageCard key={pkg.id} {...pkg} />
          ))}
        </PackagesContainer>
      </PageWrapper>
    </Fragment>
  );
};

export default Packages;